/**
 * @copyright Microsoft 2020
 */
import { getLocation, getDocument, getWindow, hasDocument, isFunction } from "@microsoft/applicationinsights-core-js";
import { findClosestAnchor, isValueAssigned } from "./common/Utils";
var clickCaptureInputTypes = { BUTTON: true, CHECKBOX: true, RADIO: true, RESET: true, SUBMIT: true };
/**
 * Get Image href of a given HTMLImageElement
 * @param element - An html image element
 * @returns Href value.
 */
export function getImageHref(element) {
    var temp = element;
    if (temp) {
        var parent = findClosestAnchor(temp);
        if (parent.length === 1) {
            var firstParent = parent[0];
            if (firstParent.href) {
                return firstParent.href;
            }
            else if (firstParent.src) {
                return (firstParent.src);
            }
        }
    }
    return "";
}
/**
 * Get click target
 * @returns Click target URI
 */
export function getClickTarget(element) {
    var clickTarget = "";
    switch (element.tagName) {
        case "A":
        case "AREA":
            clickTarget = element.href || "";
            break;
        case "IMG":
            clickTarget = getImageHref(element);
            break;
        case "INPUT":
            var type = element.type;
            if (type && (clickCaptureInputTypes[type.toUpperCase()])) {
                var loc = getLocation() || {};
                if (element.form) {
                    clickTarget = element.form.action || (loc.pathname || "");
                }
                else {
                    clickTarget = loc.pathname || "";
                }
            }
            break;
        default:
            break;
    }
    return clickTarget;
}
/**
 * Execute callback when DOM finish loading
 */
export function onDomLoaded(callback) {
    onDomReadyDo(function () {
        if (hasDocument && document.readyState === "complete") {
            callback();
        }
        else {
            var win = getWindow();
            if (win) {
                if (win.addEventListener) {
                    win.addEventListener("load", function () {
                        callback();
                    }); // NB **not** 'onload'
                }
                else if (win.attachEvent) {
                    win.attachEvent("onload", function () {
                        callback();
                    }); // IE8
                }
            }
        }
    });
}
// use smallest domready ever for IE8. When IE8 is deprecated, use addEventListener('DomContentLoaded')
function onDomReadyDo(f) {
    /// <summary> fires function f on domRead </summary>
    /// <param type='function'>function to call on domRead</param>
    var doc = getDocument() || {};
    /in/.test(doc.readyState) ? setTimeout(function () { onDomReadyDo(f); }, 100) : f.call();
}
/**
 * Gets the pageName from the DOM or by calling a override if set.
 * @param config - configuration object
 * @returns Page name.
 */
export function getPageName(config, overrideValues) {
    /// <summary>
    ///  Gets the pageName from the DOM or by calling a override if set.
    /// </summary>
    if (overrideValues && overrideValues.pageName) {
        return overrideValues.pageName;
    }
    else if (config.callback && isFunction(config.callback.pageName)) {
        return config.callback.pageName();
    }
    else if (config.coreData && config.coreData.pageName) {
        return config.coreData.pageName;
    }
    else {
        var doc = getDocument();
        return doc && doc.title || "";
    }
}
/**
 * Sanitize URL values
 * @param config - Configuration
 * @param location - window.location or document.location
 * @returns Flag indicating if an element is market PII.
 */
export function sanitizeUrl(config, location) {
    if (!location) {
        return null;
    }
    var url = location.protocol + "//" + (location.hostname || location.host) + // location.hostname is not supported on Opera and Opera for Android
        (isValueAssigned(location.port) ? ":" + location.port : "") +
        location.pathname;
    return url;
}
/**
 * Get URI, sanitize the value if configured on
 * @param config - Configuration
 * @param location - window.location or document.location
 * @returns Flag indicating if an element is market PII.
 */
export function getUri(config, location) {
    if (config.coreData && config.coreData.requestUri && config.coreData.requestUri !== "") {
        return config.coreData.requestUri;
    }
    return sanitizeUrl(config, location);
}
//# sourceMappingURL=DataCollector.js.map